import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import TaskFormDialog from "@/components/dialogs/TaskFormDialog";
import TaskDetailSheet from "@/components/sheets/TaskDetailSheet";
import TeamNotesPanel from "@/components/TeamNotesPanel";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";

const STATUS_TABS = ["All", "To Do", "In Progress", "Review", "Done"];
const SECTION_TABS = [
  { value: "tasks", label: "Tasks" },
  { value: "group", label: "Group Tasks" },
  { value: "meeting", label: "Meeting Notes" },
  { value: "gtm", label: "GTM" },
];

export default function TaskBoard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const [section, setSection] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(searchParams.get("open"));

  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    } else {
      api.get("/clients/my-assigned").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const employeesMap = useMemo(() => {
    const map = Object.fromEntries(employees.map((e) => [e.id, e.name]));
    if (user && !isAdmin) map[user.id] = user.name;
    return map;
  }, [employees, user, isAdmin]);
  const clientsMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.company_name])), [clients]);

  const filtered = tasks.filter(
    (t) => (statusFilter === "All" || t.status === statusFilter) && (priorityFilter === "All" || t.priority === priorityFilter)
  );

  const groupedTasks = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t.group_id) return;
      if (!map[t.group_id]) map[t.group_id] = { group_id: t.group_id, group_title: t.group_title, tasks: [] };
      map[t.group_id].tasks.push(t);
    });
    return Object.values(map);
  }, [tasks]);

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Task Board">
      <Tabs value={section} onValueChange={setSection} className="mb-5">
        <TabsList data-testid="task-board-section-tabs">
          {SECTION_TABS.map((s) => (
            <TabsTrigger key={s.value} value={s.value} data-testid={`section-tab-${s.value}`}>{s.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {section === "tasks" && (
        <>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList data-testid="task-status-tabs">
                {STATUS_TABS.map((s) => (
                  <TabsTrigger key={s} value={s} data-testid={`task-status-tab-${s.replace(/\s/g, "-")}`}>{s}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-3">
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger data-testid="task-priority-filter" className="w-36"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Priorities</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button data-testid="new-task-button" onClick={() => setAddOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
                  <Plus className="h-4 w-4 mr-1.5" /> New Task
                </Button>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="task-board-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                  <th className="px-5 py-3 font-medium">Task Name</th>
                  {isAdmin && <th className="px-5 py-3 font-medium">Assignee</th>}
                  <th className="px-5 py-3 font-medium">Priority</th>
                  <th className="px-5 py-3 font-medium">Deadline</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  {isAdmin && <th className="px-5 py-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-[#4B5563]">No tasks match these filters.</td></tr>
                )}
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`task-row-${t.id}`}
                    className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200"
                  >
                    <td onClick={() => setSelectedId(t.id)} className="px-5 py-3 font-medium text-[#1C1C1E] max-w-[280px] truncate cursor-pointer">{t.title}</td>
                    {isAdmin && <td onClick={() => setSelectedId(t.id)} className="px-5 py-3 text-[#4B5563] cursor-pointer">{employeesMap[t.assignee_id] || "Unassigned"}</td>}
                    <td onClick={() => setSelectedId(t.id)} className="px-5 py-3 cursor-pointer"><StatusBadge value={t.priority} /></td>
                    <td onClick={() => setSelectedId(t.id)} className="px-5 py-3 text-[#4B5563] cursor-pointer">{t.deadline || "—"}</td>
                    <td onClick={() => setSelectedId(t.id)} className="px-5 py-3 cursor-pointer"><StatusBadge value={t.status} /></td>
                    {isAdmin && (
                      <td className="px-5 py-3">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`task-delete-${t.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{t.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteTask(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "group" && (
        <div className="space-y-4" data-testid="group-tasks-panel">
          {groupedTasks.length === 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-lg py-12 text-center text-[#4B5563]" data-testid="group-tasks-empty-state">
              <Users className="h-6 w-6 mx-auto mb-2 text-[#76C121]" />
              No group tasks allocated yet. Toggle "Assign to multiple employees" in New Task to create one.
            </div>
          )}
          {groupedTasks.map((g) => {
            const total = g.tasks.length;
            const done = g.tasks.filter((t) => t.status === "Done").length;
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <div key={g.group_id} data-testid={`group-task-row-${g.group_id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{g.group_title}</h3>
                  <span className="text-xs text-[#4B5563]">{done}/{total} completed</span>
                </div>
                <div className="h-2 rounded-full bg-[#F8F9FA] overflow-hidden mb-4">
                  <div className="h-full rounded-full bg-[#76C121]" style={{ width: `${pct}%` }} />
                </div>
                <div className="divide-y divide-[#E2E8F0]">
                  {g.tasks.map((t) => (
                    <div key={t.id} onClick={() => setSelectedId(t.id)} data-testid={`group-task-assignee-${t.id}`} className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-[#F8F9FA] transition-colors duration-200 px-2 rounded-md">
                      <span className="text-sm text-[#1C1C1E]">{employeesMap[t.assignee_id] || "Unassigned"}</span>
                      <StatusBadge value={t.status} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {section === "meeting" && <TeamNotesPanel category="meeting" clients={clients} clientsMap={clientsMap} />}
      {section === "gtm" && <TeamNotesPanel category="gtm" clients={clients} clientsMap={clientsMap} />}

      <TaskFormDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <TaskDetailSheet
        taskId={selectedId}
        open={!!selectedId}
        onOpenChange={(v) => !v && setSelectedId(null)}
        onUpdated={load}
        isAdmin={isAdmin}
        employeesMap={employeesMap}
        clientsMap={clientsMap}
      />
    </DashboardLayout>
  );
}
